import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import './Breadcrumbs.css';

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
  className?: string;
}

/**
 * Breadcrumb trail for hierarchical navigation.
 *
 * Earlier items render as links; the last item is plain text representing
 * the current page. Items without a `to` are also rendered as plain text.
 *
 * @example
 * ```tsx
 * <Breadcrumbs
 *   items={[
 *     { label: 'Development', to: '/development/repos' },
 *     { label: 'Fleet', to: '/monitor/fleet' },
 *     { label: 'elasticenergy/elastic-router' },
 *   ]}
 * />
 * ```
 */
export function Breadcrumbs({ items, className = '' }: BreadcrumbsProps) {
  const classNames = ['ui-breadcrumbs', className].filter(Boolean).join(' ');
  return (
    <nav className={classNames} aria-label="Breadcrumb">
      <ol className="ui-breadcrumbs__list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <Fragment key={`${item.label}-${index}`}>
              <li className="ui-breadcrumbs__item">
                {isLast || !item.to ? (
                  <span
                    className="ui-breadcrumbs__current"
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item.label}
                  </span>
                ) : (
                  <Link to={item.to} className="ui-breadcrumbs__link">
                    {item.label}
                  </Link>
                )}
              </li>
              {!isLast && (
                <li aria-hidden="true" className="ui-breadcrumbs__separator">
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </li>
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

Breadcrumbs.displayName = 'Breadcrumbs';
